import { useCallback, useMemo, useState } from "react";
import { DMAutomationPlatform, DMAutomationTrigger } from "@markit/common.types";
import {
  GetDateWithTime,
  getDMAutomationTriggerTitle,
} from "@markit/common.utils";
import { useTheme } from "../../../../hooks/useTheme";
import useAsyncOnMount from "../../../../hooks/useAsyncEffectOnMount";
import StandardBorderedContainer from "../../../Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import Skeleton from "react-loading-skeleton";
import {
  getDMAutomationTriggerSubmissionCount,
  getDMAutomationUniqueContactCount,
} from "../../../../utils/dmAutomationUtils";
import { useSelector } from "react-redux";
import { getAccountSlice } from "../../../../redux/slices/accountSlice";
import { SocialPlatformThumbnailItem } from "../../../DisplayItem/AutoDM/SocialPlatformThumbnailItem";

type DMAutomationDashboardAnalyticsProps = {
  automationId: string;
  hasCampaign: boolean;
  triggers: DMAutomationTrigger[];
  submissionsCount: number;
  platform: DMAutomationPlatform;
};

const DMAutomationDashboardAnalytics = (
  props: DMAutomationDashboardAnalyticsProps
) => {
  const { automationId, hasCampaign, triggers, submissionsCount, platform } =
    props;
  const { theme } = useTheme();
  const { accountData, instagramAccounts, facebookPages } =
    useSelector(getAccountSlice);

  // null while the per-trigger fetch is in flight (drives the skeleton)
  const [triggerSentCounts, setTriggerSentCounts] = useState<Record<
    string,
    number
  > | null>(null);
  const [uniqueContacts, setUniqueContacts] = useState<number | null>(null);

  const styles = {
    bigNumbers: { fontSize: 32 },
    overallContainerView: {
      width: "100%",
      backgroundColor: theme.SecondaryBG.backgroundColor,
      borderRadius: 20,
      paddingBlock: 14,
    },
  };

  const triggeredTotal = useMemo(
    () =>
      triggers.reduce((sum, trigger) => sum + (trigger.numTriggered ?? 0), 0),
    [triggers]
  );

  useAsyncOnMount(async () => {
    if (!automationId || !hasCampaign) {
      return;
    }
    const [perTrigger, uniqueContactCount] = await Promise.all([
      Promise.all(
        triggers.map(async (trigger) => {
          const count = await getDMAutomationTriggerSubmissionCount(
            accountData.uid,
            automationId,
            trigger.id
          );
          return [trigger.id, count] as const;
        })
      ),
      getDMAutomationUniqueContactCount(accountData.uid, automationId),
    ]);
    setTriggerSentCounts(Object.fromEntries(perTrigger));
    setUniqueContacts(uniqueContactCount);
  });

  const renderTriggerItem = useCallback(
    (trigger: DMAutomationTrigger) => {
      const accountLabel =
        platform === DMAutomationPlatform.FACEBOOK
          ? facebookPages.find((page) => page.id === trigger.igAccountId)
              ?.pageName
          : instagramAccounts.find(
              (account) => account.id === trigger.igAccountId
            )?.username;
      return (
        <div key={trigger.id} className="AlignedRow" style={{ gap: 14 }}>
          <div style={{ minWidth: 55 }}>
            <SocialPlatformThumbnailItem
              mediaUrl={trigger.mediaUrl}
              size={50}
              platform={platform}
            />
          </div>
          <div className="ColumnNormal" style={{ gap: 4 }}>
            <span className="bodyMedium">
              {getDMAutomationTriggerTitle(trigger)}
            </span>
            <div className="AlignedRow" style={{ gap: 4 }}>
              <span className="bodySmallMedium">
                {accountLabel ? `@${accountLabel}` : "No Connected Account"}
              </span>
              <span className="smallBodySubtext">
                {GetDateWithTime(trigger.createdAt, true)}
              </span>
            </div>
            <span className="smallBodySubtext">
              {trigger.numTriggered ?? 0} Triggered
            </span>
            {hasCampaign ? (
              <div className="AlignedRow" style={{ gap: 4 }}>
                {triggerSentCounts === null ? (
                  <Skeleton width={16} height={12} />
                ) : (
                  <span className="smallBodySubtext">
                    {triggerSentCounts[trigger.id] ?? 0}
                  </span>
                )}
                <span className="smallBodySubtext">Sent</span>
              </div>
            ) : null}
          </div>
        </div>
      );
    },
    [hasCampaign, platform, instagramAccounts, facebookPages, triggerSentCounts]
  );

  return (
    <div className="RowNormal" style={{ gap: 30, flex: 1 }}>
      <div className="ColumnNormal" style={{ gap: 14, flex: "0 0 50%" }}>
        <span className="sectionTitle">Analytics</span>
        <StandardBorderedContainer
          containerStyles={styles.overallContainerView}
        >
          <div className="ColumnNormal" style={{ gap: 14 }}>
            <div className="AlignedRow" style={{ paddingInline: 14 }}>
              <div className="ColumnNormal" style={{ marginRight: 24 }}>
                <span style={styles.bigNumbers}>{triggeredTotal}</span>
                <span className="smallBodySubtext">Triggered</span>
              </div>
              {hasCampaign ? (
                <div className="ColumnNormal" style={{ marginRight: 24 }}>
                  <span style={styles.bigNumbers}>{submissionsCount}</span>
                  <span className="smallBodySubtext">Sent</span>
                </div>
              ) : null}
            </div>
            <HorizontalDivider altMargin={0} />
            <div
              className="ColumnNormal"
              style={{ gap: 14, paddingInline: 14 }}
            >
              <span className="sectionTitle">Trigger Breakdown</span>
              {triggers.map((trigger) => renderTriggerItem(trigger))}
            </div>
            {hasCampaign ? (
              <>
                <HorizontalDivider altMargin={0} />
                <div
                  className="ColumnNormal"
                  style={{ gap: 14, paddingInline: 14 }}
                >
                  <span className="sectionTitle">Audience</span>
                  <div className="AlignedRow" style={{ gap: 14 }}>
                    <div style={{ fontSize: "24px", minWidth: 55 }}>
                      {uniqueContacts === null ? <Skeleton /> : uniqueContacts}
                    </div>
                    <div className="ColumnNormal" style={{ gap: 2 }}>
                      <span className="bodyMedium">Phone Numbers Captured</span>
                      <span className="smallBodySubtext">
                        You can text {uniqueContacts} unique numbers from this
                        DM automation
                      </span>
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </StandardBorderedContainer>
      </div>
    </div>
  );
};

export default DMAutomationDashboardAnalytics;
